/**
 * standards-coverage.js — render the FAQ standards-coverage surfaces
 * from /static/standards-coverage.json (live fetch at page load).
 *
 * Two surfaces per design Q1 + coordinator revised taxonomy:
 *   Surface 1: per-namespace standards-coverage table with expandable
 *              per-clause IMPLEMENTED/KNOWN_GAP rows
 *   Surface 2: DocAble features beyond the standards (flat list, no
 *              coverage percentage)
 *
 * Live-fetch fallback discipline per CLAUDE.md rule #17: if the fetch
 * fails, render a closed-set fallback message. The fallback string IS
 * the served-page smoke gate's banned-strings tuple — any new fallback
 * string added here must be added to the gate too.
 *
 * Design: docs/design/standards-manifest-and-faq-260513.md.
 */
/** Mirror of `MicrosoftParityEmitter`'s wire state values (design §D.3). */
const MS_PARITY_STATE_LABELS = Object.freeze({
    GREEN_WITHIN_COVERAGE: 'Green (within coverage)',
    NOT_GREEN: 'Not green',
    INDETERMINATE: 'Indeterminate',
});
/** Minimal HTML-escape — module-scope duplicate of the IIFE-local `escHtml`
 * below (same small helper pattern already duplicated per-module across
 * `web/static/*.ts`; not worth a shared-util extraction for 4 lines). */
function escHtmlMsParity(s) {
    if (s === undefined || s === null)
        return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * Microsoft-Checker parity badge (design §D.4) — pure data→HTML builder for
 * the per-document `microsoft_parity` block. Vendor-checker framing: the
 * state pill + the "N of M covered" denominator (INV-D2 — the claim is
 * inseparable from its denominator) + the disclaimer that this is NOT a
 * formal-standard / Microsoft-endorsed signal. No DOM/events.
 *
 * MODULE-SCOPE EXPORT (not IIFE-private): both `standards-coverage.ts`'s own
 * `renderSurface1` (guarded on `data.microsoft_parity`) AND `examples.ts`'s
 * per-sample /examples card renderer call this directly — the /examples path
 * needs a real import, not the `__test`-only escape hatch (which is
 * documented as tree-shaken from the delivery bundle and non-production).
 */
export function renderMicrosoftParityBadge(block) {
    const label = MS_PARITY_STATE_LABELS[block.state] || block.state;
    const stateClass = 'ms-parity-' + String(block.state).toLowerCase();
    const gapItems = (block.gaps || [])
        .map(function (g) {
        const tag = g.disclosed ? ' (disclosed)' : '';
        return '<li>' + escHtmlMsParity(g.clause) + ' <span class="ms-parity-sev">'
            + escHtmlMsParity(g.ms_severity) + '</span>' + tag + '</li>';
    })
        .join('');
    const gapsBlock = gapItems
        ? '<ul class="ms-parity-gaps">' + gapItems + '</ul>'
        : '';
    return ''
        + '<div class="ms-parity-badge ' + stateClass + '" '
        + 'role="group" aria-label="Microsoft Accessibility Checker parity">'
        + '<div class="ms-parity-header">'
        + '<span class="ms-parity-vendor">Microsoft Checker</span>'
        + '<span class="ms-parity-state">' + escHtmlMsParity(label) + '</span>'
        + '</div>'
        + '<div class="ms-parity-denominator">Green on '
        + escHtmlMsParity(block.covered_count) + ' of ' + escHtmlMsParity(block.in_scope_count)
        + ' Microsoft rules ADAtool covers</div>'
        + gapsBlock
        + '<p class="ms-parity-disclaimer">' + escHtmlMsParity(block.disclaimer) + '</p>'
        + '</div>';
}
/**
 * Test-only escape hatch — exposes the IIFE-private PURE builder functions
 * (data → HTML-string transforms; no DOM, no `document`, no events) for
 * unit tests, without refactoring the closure or touching the render path.
 *
 * WHY module-level mutable object: fmtPct / escHtml / renderClauses /
 * renderLegend / renderFilterBar are defined inside the IIFE because they
 * close over COVERAGE_STATE_BADGES / CoverageState5 / ClauseStatus. The IIFE
 * assigns them onto this object on execution so a `node`-environment seam test
 * that imports this module can call them directly. This is the same escape-hatch
 * pattern as examples.ts (ts-jsdom-coverage-260626 Pilot C) — behavior-preserving:
 * production code never reads __test; it is tree-shaken from the delivery bundle.
 *
 * WHY optional types: at import time (before the IIFE runs) the fields are
 * undefined; by the time any test body executes the IIFE has already run.
 * Tests MUST assert non-null before calling (see standards-coverage.seam.test.ts).
 *
 * Per docs/epics/active/prod-coverage-ts-260717/main.md Phase 2 §2c —
 * minimal pure-builder exposure, NOT a view-model extraction.
 */
export const __test = {};
(function () {
    'use strict';
    const FALLBACK_STRINGS = Object.freeze({
        // The smoke gate looks for the absence of this exact substring on
        // a populated standards-coverage container. Don't change without
        // updating the gate's banned-strings tuple.
        UNAVAILABLE: 'Standards coverage data is temporarily unavailable',
    });
    // Mirror of Python ClauseStatus / C# ClauseStatus — values must match the
    // strings emitted into standards-coverage.json by the Python generator.
    // Retained for the legacy binary status field used by the per-namespace
    // summary row; the multi-state model (CoverageState5 below) supersedes it
    // for per-clause display.
    const ClauseStatus = Object.freeze({
        IMPLEMENTED: 'implemented',
        KNOWN_GAP: 'known_gap',
        UNRESOLVED: 'unresolved',
    });
    // Coverage model mirror of C#
    // AdaTool.Standards.Manifests.CoverageState5. Strings match the wire
    // output of StandardsCoverageEmitter.StateToWire. The name's '5' is a
    // historical artifact of the original member count (260514); the set is
    // now seven (PARTIAL added 260515). See
    // docs/design/verb-standards-tier-260514.md §3 + §4 and
    // docs/design/partial-coverage-tier-260515.md.
    const CoverageState5 = Object.freeze({
        FULLY_ADDRESSED: 'fully_addressed',
        BEST_EFFORT: 'best_effort',
        DETECTED_NOT_REMEDIATED: 'detected_not_remediated',
        ASPIRATIONAL: 'aspirational',
        PARTIAL: 'partial',
        OUT_OF_SCOPE: 'out_of_scope',
        COVERAGE_GAP: 'coverage_gap',
    });
    // Per-state badge metadata. Labels + colors per
    // docs/design/verb-standards-tier-260514.md §4 (tooltip-copy table).
    // Contrast ratios on near-white (#ffffff):
    //   #1a7f37 (green)  ~5.6:1 PASS AA normal text
    //   #9a6700 (amber)  ~4.5:1 PASS AA normal text (borderline)
    //   #c34e00 (orange) ~5.1:1 PASS AA normal text
    //   #0e6ea6 (blue)   ~4.7:1 PASS AA normal text
    //   #0a6e6e (teal)   ~6.0:1 PASS AA normal text
    //   #6e7781 (grey)   ~4.6:1 PASS AA normal text
    const COVERAGE_STATE_BADGES = Object.freeze({
        [CoverageState5.FULLY_ADDRESSED]: {
            label: '✓ Addressed',
            cssClass: 'coverage-badge-fully-addressed',
            color: '#1a7f37',
            tooltip: 'DocAble automatically remediates violations of this requirement. For AI-assisted items, our post-validation pass confirms the structural requirement is met.',
        },
        [CoverageState5.BEST_EFFORT]: {
            label: '~ Best effort', // ~
            cssClass: 'coverage-badge-best-effort',
            color: '#9a6700',
            tooltip: "DocAble's AI addresses this requirement. Outputs are usually correct but we recommend manual review for high-stakes documents.",
        },
        [CoverageState5.DETECTED_NOT_REMEDIATED]: {
            label: '△ Detected, not fixed',
            cssClass: 'coverage-badge-detected-only',
            color: '#c34e00',
            tooltip: 'DocAble detects and reports violations of this requirement in the accessibility report, but does not automatically fix them.',
        },
        [CoverageState5.ASPIRATIONAL]: {
            label: '◇ Planned',
            cssClass: 'coverage-badge-aspirational',
            color: '#0e6ea6',
            tooltip: 'DocAble plans to address this requirement.',
        },
        [CoverageState5.PARTIAL]: {
            label: '◐ Partial',
            cssClass: 'coverage-badge-partial',
            color: '#0a6e6e',
            tooltip: 'DocAble fully delivers our axis of this requirement (the static-file content/structure/prerequisite axis); a complementary axis (visual reflow, viewer keyboard handling, platform rendering) is structurally the document reader’s or platform’s.',
        },
        [CoverageState5.OUT_OF_SCOPE]: {
            label: '— Not applicable',
            cssClass: 'coverage-badge-out-of-scope',
            color: '#6e7781',
            tooltip: 'This requirement is enforced by the document reader, web platform, or time-based media tooling rather than the static document content.',
        },
        [CoverageState5.COVERAGE_GAP]: {
            label: '— Coverage gap',
            cssClass: 'coverage-badge-coverage-gap',
            color: '#6e7781',
            tooltip: 'DocAble does not currently address this requirement and there is no roadmap commitment yet.',
        },
    });
    const COVERAGE_JSON_URL = '/static/standards-coverage.json';
    function fmtPct(impl, total) {
        if (!total)
            return '0%';
        return Math.round((impl * 100) / total) + '%';
    }
    function escHtml(s) {
        if (s === undefined || s === null)
            return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }
    function renderSurface1(container, data) {
        const namespaces = data.namespaces || {};
        const schemaVersion = data.schema_version || 1;
        const fiveStateMode = schemaVersion >= 2;
        const rows = [];
        rows.push('<div class="std-coverage-intro">');
        rows.push('<p>We are transparent about what we check. Below is the clause-by-clause coverage matrix for each standard we target. Where we do not yet check a clause, we list the reason.</p>');
        if (fiveStateMode) {
            rows.push(renderLegend());
            rows.push(renderFilterBar());
        }
        rows.push('</div>');
        // Microsoft-Checker parity badge (design §D.4) — CALLED here, guarded on
        // data.microsoft_parity being present. The generator does not emit this
        // field today (see the CoverageData doc-comment above); this is
        // forward-wiring so the badge activates the moment upstream data lands,
        // without fabricating a data path now (rule: do not invent a fake seam).
        if (data.microsoft_parity) {
            rows.push(renderMicrosoftParityBadge(data.microsoft_parity));
        }
        rows.push('<div class="std-coverage-table-wrap">');
        rows.push('<table class="std-coverage-table" aria-label="Standards coverage by namespace">');
        if (fiveStateMode) {
            rows.push('<thead><tr>'
                + '<th scope="col">Standard</th>'
                + '<th scope="col" title="Fully addressed">✓</th>'
                + '<th scope="col" title="Best effort">~</th>'
                + '<th scope="col" title="Detected, not fixed">△</th>'
                + '<th scope="col" title="Planned">◇</th>'
                + '<th scope="col" title="Partial">◐</th>'
                + '<th scope="col" title="Not applicable">—</th>'
                + '<th scope="col">Total</th>'
                + '</tr></thead>');
        }
        else {
            rows.push('<thead><tr><th scope="col">Standard</th><th scope="col">Implemented</th><th scope="col">Total</th><th scope="col">Coverage</th></tr></thead>');
        }
        rows.push('<tbody>');
        const namespaceOrder = [
            'WCAG-2.1', 'WCAG-2.2',
            'PDFUA-1', 'PDFUA-2',
            'MATTERHORN-1.04',
            'SECTION-508', 'EN-301-549',
            'OFFICE-PPTX', 'OFFICE-DOCX', 'OFFICE-XLSX',
            'DocAble',
        ];
        for (const ns of namespaceOrder) {
            const bucket = namespaces[ns];
            if (!bucket)
                continue;
            const detailId = 'std-clauses-' + ns.replace(/[^a-zA-Z0-9]/g, '-');
            const colSpan = fiveStateMode ? 8 : 4;
            rows.push('<tr class="std-coverage-summary-row">');
            rows.push('<th scope="row"><button type="button" class="std-coverage-toggle" aria-controls="' + detailId + '" aria-expanded="false">');
            rows.push(escHtml(bucket.name || ns) + ' <span class="std-coverage-toggle-icon" aria-hidden="true">▸</span>');
            rows.push('</button></th>');
            if (fiveStateMode) {
                rows.push('<td>' + (bucket.fully_addressed || 0) + '</td>');
                rows.push('<td>' + (bucket.best_effort || 0) + '</td>');
                rows.push('<td>' + (bucket.detected_not_remediated || 0) + '</td>');
                rows.push('<td>' + (bucket.aspirational || 0) + '</td>');
                rows.push('<td>' + (bucket.partial || 0) + '</td>');
                rows.push('<td>' + (bucket.out_of_scope || 0) + '</td>');
                rows.push('<td>' + bucket.total + '</td>');
            }
            else {
                rows.push('<td>' + (bucket.implemented || 0) + '</td>');
                rows.push('<td>' + bucket.total + '</td>');
                rows.push('<td>' + fmtPct(bucket.implemented || 0, bucket.total) + '</td>');
            }
            rows.push('</tr>');
            // Hidden per-clause row (toggled open by the button).
            rows.push('<tr class="std-coverage-detail-row" hidden>');
            rows.push('<td colspan="' + colSpan + '"><div id="' + detailId + '" class="std-coverage-clauses">');
            rows.push(renderClauses(bucket.clauses || [], fiveStateMode));
            rows.push('</div></td>');
            rows.push('</tr>');
        }
        rows.push('</tbody>');
        rows.push('</table>');
        rows.push('</div><!-- /.std-coverage-table-wrap -->');
        container.innerHTML = rows.join('');
        if (fiveStateMode)
            wireFilterBar(container);
        // WHY: aria-busy="true" was set on the container in help.html to signal
        // "live region loading". Clear it now that content is populated so AT
        // users receive the region as fully rendered, not perpetually loading.
        container.setAttribute('aria-busy', 'false');
        // WHY: a static legend is rendered in help.html as the pre-JS-render
        // smoke-gate's grep target and as a graceful-degradation legend if
        // the JS-rendered legend never appears. Once we've drawn the JS
        // legend (renderLegend, inside the intro region above), the static
        // legend becomes a verbatim duplicate — screen-reader users navigating
        // by region hear two "Coverage state legend" landmarks. Hide it now.
        // renderFallback re-exposes it via removeAttribute('hidden').
        if (fiveStateMode)
            hideStaticLegend();
        // Wire up expand/collapse.
        const toggles = container.querySelectorAll('.std-coverage-toggle');
        for (const btn of toggles) {
            btn.addEventListener('click', function () {
                const expanded = btn.getAttribute('aria-expanded') === 'true';
                btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
                const iconEl = btn.querySelector('.std-coverage-toggle-icon');
                if (iconEl)
                    iconEl.textContent = expanded ? '▸' : '▾';
                const summaryRow = btn.closest('tr.std-coverage-summary-row');
                const detailRow = summaryRow ? summaryRow.nextElementSibling : null;
                if (detailRow) {
                    if (expanded)
                        detailRow.setAttribute('hidden', '');
                    else
                        detailRow.removeAttribute('hidden');
                }
            });
        }
    }
    function renderClauses(clauses, fiveStateMode) {
        if (!clauses.length) {
            return '<p>No clauses listed.</p>';
        }
        const out = ['<ul class="std-coverage-clause-list">'];
        for (const c of clauses) {
            // Five-state mode: render the five-state badge for c.state.
            // Fall back to legacy binary status for schema_version 1.
            let badge = null;
            let stateValue = null;
            if (fiveStateMode && c.state) {
                badge = COVERAGE_STATE_BADGES[c.state];
                stateValue = c.state;
            }
            let statusLabel;
            let statusClass;
            if (badge !== null && badge !== undefined) {
                statusLabel = badge.label;
                statusClass = badge.cssClass;
            }
            else {
                // Legacy path.
                statusClass = c.status === ClauseStatus.IMPLEMENTED
                    ? 'std-clause-impl' : 'std-clause-gap';
                statusLabel = (() => {
                    switch (c.status) {
                        case ClauseStatus.IMPLEMENTED: return '✓ Implemented';
                        case ClauseStatus.KNOWN_GAP: return '— Known gap';
                        case ClauseStatus.UNRESOLVED: return '— Pending';
                        default:
                            window.adatool?.log?.warn('[standards-coverage] unknown clause status:', c.status);
                            return '— Unknown status: ' + c.status;
                    }
                })();
            }
            const lvl = c.level ? ' <span class="std-clause-level">(' + escHtml(c.level) + ')</span>' : '';
            const stateAttr = stateValue ? ' data-state="' + escHtml(stateValue) + '"' : '';
            const colorStyle = badge ? ' style="color:' + badge.color + ';"' : '';
            const tooltipAttr = badge ? ' title="' + escHtml(badge.tooltip) + '"' : '';
            out.push('<li class="' + statusClass + '"' + stateAttr + '>');
            // WHY: no aria-label here — textContent carries the same accessible
            // name (anti-pattern: byte-identical aria-label adds nothing for AT
            // and discards the visible-text fallback if escaping diverges).
            // title= remains the carrier of the longer per-state description.
            out.push('<span class="std-clause-status"' + colorStyle + tooltipAttr
                + '>' + escHtml(statusLabel) + '</span> ');
            out.push('<strong>' + escHtml(c.clause) + '</strong> — ' + escHtml(c.name) + lvl);
            if (c.summary) {
                out.push('<div class="std-clause-summary">' + escHtml(c.summary) + '</div>');
            }
            // Wave 2d (260514): rule_ids + implementing_verbs from the C# emitter.
            if (Array.isArray(c.rule_ids) && c.rule_ids.length) {
                out.push('<div class="std-clause-rule-ids"><strong>Detected by:</strong> ' + c.rule_ids.map(escHtml).join(', ') + '</div>');
            }
            if (Array.isArray(c.implementing_verbs) && c.implementing_verbs.length) {
                out.push('<div class="std-clause-verbs"><strong>Repaired by:</strong> ' + c.implementing_verbs.map(escHtml).join(', ') + '</div>');
            }
            if (c.state_rationale) {
                out.push('<div class="std-clause-rationale">' + escHtml(c.state_rationale) + '</div>');
            }
            if (c.roadmap_target && c.state === CoverageState5.ASPIRATIONAL) {
                out.push('<div class="std-clause-roadmap"><strong>Roadmap target:</strong> ' + escHtml(c.roadmap_target) + '</div>');
            }
            if (c.out_of_scope_rationale && c.state === CoverageState5.OUT_OF_SCOPE) {
                out.push('<div class="std-clause-oos"><strong>Why not applicable:</strong> ' + escHtml(c.out_of_scope_rationale) + '</div>');
            }
            if (c.partial_rationale && c.state === CoverageState5.PARTIAL) {
                out.push('<div class="std-clause-partial"><strong>What we deliver vs. what the viewer/platform enforces:</strong> ' + escHtml(c.partial_rationale) + '</div>');
            }
            // Legacy gap rationale (for schema_version 1 fallback).
            if (!fiveStateMode && c.status === ClauseStatus.KNOWN_GAP && c.rationale) {
                out.push('<div class="std-clause-rationale"><strong>Why not yet covered:</strong> ' + escHtml(c.rationale) + '</div>');
            }
            if (c.source_url) {
                out.push('<div class="std-clause-source"><a href="' + escHtml(c.source_url) + '" rel="noopener">Reference</a></div>');
            }
            out.push('</li>');
        }
        out.push('</ul>');
        return out.join('');
    }
    // Render the five-state legend block. The labels appear here verbatim
    // so the served-page smoke gate can assert their presence (CLAUDE.md
    // rule #17). Per docs/design/verb-standards-tier-260514.md §4.
    function renderLegend() {
        const states = [
            CoverageState5.FULLY_ADDRESSED,
            CoverageState5.BEST_EFFORT,
            CoverageState5.DETECTED_NOT_REMEDIATED,
            CoverageState5.ASPIRATIONAL,
            CoverageState5.PARTIAL,
            CoverageState5.OUT_OF_SCOPE,
        ];
        const out = ['<div class="std-coverage-legend" aria-label="Coverage state legend">'];
        out.push('<strong class="std-coverage-legend-title">Coverage states:</strong>');
        out.push('<ul class="std-coverage-legend-list">');
        for (const state of states) {
            const badge = COVERAGE_STATE_BADGES[state];
            if (!badge)
                continue;
            out.push('<li class="' + badge.cssClass + '">');
            out.push('<span class="std-coverage-legend-swatch" style="color:' + badge.color + ';">'
                + escHtml(badge.label) + '</span> ');
            out.push('<span class="std-coverage-legend-text">' + escHtml(badge.tooltip) + '</span>');
            out.push('</li>');
        }
        out.push('</ul>');
        out.push('</div>');
        return out.join('');
    }
    // Render the filter bar above the namespace table. Each checkbox toggles
    // visibility of clauses in one of the five states.
    function renderFilterBar() {
        const states = [
            CoverageState5.FULLY_ADDRESSED,
            CoverageState5.BEST_EFFORT,
            CoverageState5.DETECTED_NOT_REMEDIATED,
            CoverageState5.ASPIRATIONAL,
            CoverageState5.PARTIAL,
            CoverageState5.OUT_OF_SCOPE,
        ];
        const out = ['<div class="std-coverage-filter-bar" role="region" aria-label="Filter clauses by state">'];
        out.push('<strong class="std-coverage-filter-title">Show states:</strong>');
        for (const state of states) {
            const badge = COVERAGE_STATE_BADGES[state];
            if (!badge)
                continue;
            const id = 'std-filter-' + state.replace(/_/g, '-');
            out.push('<label class="std-coverage-filter-toggle" for="' + id + '">');
            out.push('<input type="checkbox" id="' + id + '" data-state-filter="' + state + '" checked>');
            out.push(' <span style="color:' + badge.color + ';">' + escHtml(badge.label) + '</span>');
            out.push('</label>');
        }
        out.push('</div>');
        return out.join('');
    }
    // Wire up filter checkboxes to toggle clause visibility per state.
    function wireFilterBar(container) {
        const checkboxes = container.querySelectorAll('input[data-state-filter]');
        function refresh() {
            const visible = new Set();
            for (const cb of checkboxes) {
                if (cb.checked)
                    visible.add(cb.getAttribute('data-state-filter'));
            }
            const items = container.querySelectorAll('li[data-state]');
            for (const li of items) {
                const state = li.getAttribute('data-state');
                if (visible.has(state))
                    li.removeAttribute('hidden');
                else
                    li.setAttribute('hidden', '');
            }
        }
        for (const cb of checkboxes) {
            cb.addEventListener('change', refresh);
        }
    }
    function renderSurface2(container, data) {
        const features = data.features || [];
        const rows = [];
        rows.push('<h3>DocAble features beyond the standards</h3>');
        rows.push('<p>Capabilities that go beyond the rules in the standards above — proprietary to DocAble.</p>');
        if (!features.length) {
            rows.push('<p>(No feature entries declared.)</p>');
            container.innerHTML = rows.join('');
            return;
        }
        rows.push('<ul class="std-features-list">');
        for (const f of features) {
            rows.push('<li class="std-features-item">');
            rows.push('<strong>' + escHtml(f.name) + '</strong>');
            rows.push('<div class="std-features-summary">' + escHtml(f.summary || '') + '</div>');
            if (f.source_url) {
                rows.push('<div class="std-features-source">Source: <code>' + escHtml(f.source_url) + '</code></div>');
            }
            rows.push('</li>');
        }
        rows.push('</ul>');
        container.innerHTML = rows.join('');
        container.setAttribute('aria-busy', 'false');
    }
    function renderFallback(container) {
        container.innerHTML =
            '<p class="std-coverage-fallback">' +
                escHtml(FALLBACK_STRINGS.UNAVAILABLE) +
                '. Please refresh the page or contact support if the issue persists.</p>';
        // WHY: aria-busy="true" was set on the container in help.html. The
        // fallback path is a terminal render — clear busy so AT does not
        // announce this region as still loading.
        container.setAttribute('aria-busy', 'false');
        // WHY: the static legend was authored as the fallback-mode-visible
        // legend; if a previous successful render hid it (renderSurface1),
        // re-expose it now so users see the coverage-state vocabulary even
        // when the JS-rendered table is unavailable.
        showStaticLegend();
    }
    function hideStaticLegend() {
        const el = document.getElementById('std-coverage-static-legend');
        if (el)
            el.setAttribute('hidden', '');
    }
    function showStaticLegend() {
        const el = document.getElementById('std-coverage-static-legend');
        if (el)
            el.removeAttribute('hidden');
    }
    function init() {
        const coverageContainer = document.getElementById('standards-coverage-container');
        const featuresContainer = document.getElementById('docable-features-container');
        if (!coverageContainer && !featuresContainer)
            return; // page does not host these sections
        fetch(COVERAGE_JSON_URL, { credentials: 'omit', cache: 'no-cache' })
            .then(function (resp) {
            if (!resp.ok)
                throw new Error('fetch ' + resp.status);
            return resp.json();
        })
            .then(function (data) {
            if (coverageContainer)
                renderSurface1(coverageContainer, data);
            if (featuresContainer)
                renderSurface2(featuresContainer, data);
        })
            .catch(function (err) {
            // Log loudly for ops visibility but don't crash the page.
            window.adatool?.log?.warn('[standards-coverage] fetch failed:', err);
            if (coverageContainer)
                renderFallback(coverageContainer);
            if (featuresContainer)
                renderFallback(featuresContainer);
        });
    }
    // Wire the __test escape hatch (see module-level export above) so seam
    // tests can call the IIFE-private PURE builders via the production code
    // path. Production code never reads __test; it is tree-shaken from the
    // delivery bundle. This is behavior-preserving — the functions are not
    // altered, only referenced.
    __test.fmtPct = fmtPct;
    __test.escHtml = escHtml;
    __test.renderClauses = renderClauses;
    __test.renderLegend = renderLegend;
    __test.renderFilterBar = renderFilterBar;
    __test.renderMicrosoftParityBadge = renderMicrosoftParityBadge;
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    }
    else {
        init();
    }
})();
